import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage() {
  const { login, register } = useAuth();
  const { settings } = useSettings();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Benvenuto!");
      } else {
        await register(name, email, password);
        toast.success("Account creato con successo");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen tp-bg flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-center">
        {/* Left panel */}
        <div className="hidden lg:block space-y-8 pr-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-zinc-300">
            <Sparkles size={12} className="text-amber-400" />
            Task management in stile Apple
          </div>
          <h1 className="text-5xl font-semibold tracking-tight text-white leading-[1.05]">
            <span className="tp-brand-text">{settings.app_name || "Hi-Work"}</span> <br />
            Gestisci il lavoro <br />
            del tuo team, <br />
            <span className="text-zinc-400">senza rumore.</span>
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md">
            Una scheda per ogni cliente. Task assegnate al team con priorità a
            stelle, scadenze e commenti. Ispirato a monday.com, disegnato come
            un&apos;app Apple.
          </p>
          <ul className="space-y-3 text-sm text-zinc-300">
            {[
              "Schede clienti con task raggruppate",
              "Vista personale 'Il mio lavoro' per oggi",
              "Assegnazione a più utenti con commenti e priorità 1-5",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <CheckCircle2 size={16} className="text-emerald-400" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Form panel */}
        <div className="tp-glass-strong rounded-3xl p-8 sm:p-10 shadow-2xl tp-fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {settings.app_name || "Hi-Work"}
              </div>
              <h2 className="text-2xl font-semibold text-white mt-1">
                {mode === "login" ? "Bentornato" : "Crea account"}
              </h2>
            </div>
            <BrandLogo size={44} rounded="rounded-2xl" editable={false} />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-zinc-300 text-xs uppercase tracking-wider">
                  Nome
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white h-11"
                  placeholder="Mario Rossi"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-zinc-300 text-xs uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white h-11"
                placeholder="you@company.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-zinc-300 text-xs uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="pw"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white h-11"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"}
              disabled={loading}
              className="tp-brand-btn w-full h-11 rounded-xl font-semibold"
            >
              {loading ? "Attendere..." : mode === "login" ? "Accedi" : "Registrati"}
            </Button>

            <button
              type="button"
              data-testid="auth-mode-switch"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="w-full text-center text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {mode === "login" ? "Non hai un account? Registrati" : "Hai già un account? Accedi"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
